import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useWeb3 } from '../context/Web3Context';
import './MoreGamesSection.css';

const weiToUsd = (wei) => {
  if (!wei || wei === '0') return '0.00';
  try { return (Number(BigInt(wei)) / 1e18).toFixed(4); } catch { return '0.00'; }
};
const usdToWei = (usd) => {
  try {
    const val = parseFloat(usd);
    if (isNaN(val) || val <= 0) return null;
    return BigInt(Math.round(val * 1e18)).toString();
  } catch { return null; }
};
const bpsToX = (bps) => (bps / 10000).toFixed(2) + '×';

// ─── Mines ────────────────────────────────────────────────────────────────────
function Mines({ balanceWei, cfg, onRefresh }) {
  const [bet, setBet] = useState('');
  const [session, setSession] = useState(null);
  const [grid, setGrid] = useState([]);        // 'hidden' | 'safe' | 'mine' | 'mine-hit'
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [exploding, setExploding] = useState(false);

  const mineOpts = cfg?.mineOptions || [1,2,3,5,8];
  const [mineCount, setMineCount] = useState(() => mineOpts[0] ?? 3);

  useEffect(() => {
    if (!mineOpts.includes(mineCount)) setMineCount(mineOpts[0] ?? 3);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg]);

  useEffect(() => {
    api.get('/more-games/mines/session').then(res => {
      if (res.data.session) {
        const s = res.data.session;
        setSession(s);
        const g = Array(s.gridSize).fill('hidden');
        s.revealedTiles.forEach(i => { g[i] = 'safe'; });
        setGrid(g);
      }
    }).catch(() => {});
  }, []);

  const startGame = useCallback(async () => {
    setError(''); setResult(null); setExploding(false);
    const betWei = usdToWei(bet);
    if (!betWei) { setError('Enter a valid amount'); return; }
    setLoading(true);
    try {
      const res = await api.post('/more-games/mines/start', { betWei, mineCount });
      setSession({ ...res.data, betWei });
      setGrid(Array(res.data.gridSize).fill('hidden'));
      onRefresh();
    } catch (e) { setError(e.response?.data?.message || 'Game error'); }
    finally { setLoading(false); }
  }, [bet, mineCount, onRefresh]);

  const revealTile = useCallback(async (tileIndex) => {
    if (!session || grid[tileIndex] !== 'hidden' || loading) return;
    setLoading(true);
    try {
      const res = await api.post('/more-games/mines/reveal', { sessionId: session.sessionId, tileIndex });
      const newGrid = [...grid];
      if (res.data.isMine) {
        // Mark the hit tile first, then reveal all mines with a delay
        newGrid[tileIndex] = 'mine-hit';
        setGrid([...newGrid]);
        setExploding(true);
        setTimeout(() => {
          if (res.data.minePositions) {
            res.data.minePositions.forEach(i => { if (newGrid[i] === 'hidden') newGrid[i] = 'mine'; });
          }
          setGrid([...newGrid]);
          setResult(res.data);
          setSession(null);
          setExploding(false);
          onRefresh();
        }, 600);
      } else if (res.data.allSafeRevealed) {
        newGrid[tileIndex] = 'safe';
        if (res.data.minePositions) {
          res.data.minePositions.forEach(i => { if (newGrid[i] === 'hidden') newGrid[i] = 'mine'; });
        }
        setGrid([...newGrid]);
        setResult(res.data);
        setSession(null);
        onRefresh();
      } else {
        newGrid[tileIndex] = 'safe';
        setGrid([...newGrid]);
        setSession(prev => ({ ...prev, currentMultiplierBps: res.data.currentMultiplierBps, revealedTiles: [...(prev.revealedTiles||[]), tileIndex] }));
        onRefresh();
      }
    } catch (e) { setError(e.response?.data?.message || 'Game error'); }
    finally { setLoading(false); }
  }, [session, grid, loading, onRefresh]);

  const cashout = useCallback(async () => {
    if (!session || loading) return;
    setLoading(true);
    try {
      const res = await api.post('/more-games/mines/cashout', { sessionId: session.sessionId });
      const newGrid = [...grid];
      if (res.data.minePositions) {
        res.data.minePositions.forEach(i => { if (newGrid[i] === 'hidden') newGrid[i] = 'mine'; });
        setGrid([...newGrid]);
      }
      setResult(res.data);
      setSession(null);
      onRefresh();
    } catch (e) { setError(e.response?.data?.message || 'Game error'); }
    finally { setLoading(false); }
  }, [session, grid, loading, onRefresh]);

  const resetGame = () => { setSession(null); setGrid([]); setResult(null); setExploding(false); setError(''); };

  const revealedCount = session?.revealedTiles?.length || 0;
  const cashoutWei = session && revealedCount > 0
    ? ((BigInt(session.betWei) * BigInt(session.currentMultiplierBps)) / 10000n).toString()
    : null;

  return (
    <div className={`game-card game-card-wide mines-card${exploding ? ' mines-exploding' : ''}`}>
      <div className="game-card-header">
        <span className="game-icon">💣</span>
        <h3>Mines</h3>
        {cfg && <p className="game-desc">Avoid the mines — cash out anytime to keep your winnings</p>}
      </div>

      {!session && !result && (
        <div className="mines-setup">
          <div className="mines-mine-selector">
            <span className="mines-label">Mines on board:</span>
            {mineOpts.map(n => (
              <button key={n} className={`choice-btn ${mineCount===n?'choice-active':''}`} onClick={()=>setMineCount(n)} disabled={loading}>
                {n} 💣
              </button>
            ))}
          </div>
          <div className="mines-risk-bar">
            {mineOpts.map(n => (
              <span key={n} className={`mines-risk-dot ${n === mineCount ? 'active' : ''}`} />
            ))}
            <span className="mines-risk-label">Risk: {mineCount <= 2 ? 'Low' : mineCount <= 3 ? 'Medium' : mineCount <= 5 ? 'High' : 'Extreme'}</span>
          </div>
          <div className="game-controls">
            <div className="bet-row">
              <input className="bet-input" type="number" min="0.01" step="0.01" placeholder="Bet (USDT)" value={bet} onChange={e=>setBet(e.target.value)} disabled={loading} />
              <button className="play-btn mines-start-btn" onClick={startGame} disabled={loading||!bet}>
                {loading ? 'Starting…' : '▶ Start Game'}
              </button>
            </div>
            {error && <p className="game-error">{error}</p>}
            <p className="game-balance-hint">Balance: ${weiToUsd(balanceWei)} USDT</p>
          </div>
        </div>
      )}

      {(session || result) && (
        <>
          <div className="mines-grid" style={{ gridTemplateColumns: 'repeat(5, 1fr)' }}>
            {Array.from({ length: session?.gridSize || grid.length || 25 }).map((_, i) => {
              const state = grid[i] || 'hidden';
              const isClickable = session && state === 'hidden' && !loading;
              return (
                <button
                  key={i}
                  className={`mine-tile mine-tile-${state}${isClickable ? ' mine-tile-clickable' : ''}`}
                  onClick={() => isClickable && revealTile(i)}
                  disabled={!isClickable}
                  aria-label={state === 'hidden' ? 'Reveal tile' : state}
                >
                  <span className="mine-tile-inner">
                    {state === 'safe'     && <span className="mine-tile-gem">💎</span>}
                    {state === 'mine'     && <span className="mine-tile-bomb">💣</span>}
                    {state === 'mine-hit' && <span className="mine-tile-explode">💥</span>}
                  </span>
                </button>
              );
            })}
          </div>

          {session && (
            <div className="mines-status">
              <div className="mines-multiplier-wrap">
                <span className="mines-multiplier-label">Multiplier</span>
                <span className="mines-multiplier">
                  {revealedCount > 0 ? bpsToX(session.currentMultiplierBps) : '—'}
                </span>
              </div>
              <div className="mines-info">
                <span className="mines-hint">{revealedCount} safe tile{revealedCount !== 1 ? 's' : ''} revealed</span>
                <span className="mines-hint">Bet: ${weiToUsd(session.betWei)} USDT</span>
              </div>
              {revealedCount > 0 && (
                <button className="play-btn mines-cashout-btn" onClick={cashout} disabled={loading}>
                  {loading ? '…' : `💰 Cash Out $${weiToUsd(cashoutWei)}`}
                </button>
              )}
            </div>
          )}

          {result && (
            <div className={`game-result ${result.win ? 'game-win' : 'game-loss'}`}>
              {result.win
                ? `💰 Cashed out! You won $${weiToUsd(result.payoutWei)} USDT`
                : `💥 You hit a mine! Better luck next time.`}
            </div>
          )}

          {result && (
            <button className="play-btn mines-again-btn" onClick={resetGame}>
              🔄 Play Again
            </button>
          )}
          {error && <p className="game-error">{error}</p>}
        </>
      )}
    </div>
  );
}

// ─── Crash ────────────────────────────────────────────────────────────────────
function Crash({ balanceWei, onRefresh }) {
  const [bet, setBet] = useState('');
  const [target, setTarget] = useState('2.00');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [animMult, setAnimMult] = useState(1.00);
  const animRef = useRef(null);

  const betWei = usdToWei(bet);
  const targetBps = Math.round(parseFloat(target || '1') * 10000) || 10000;
  const potentialPayout = betWei && targetBps > 10000 ? ((BigInt(betWei) * BigInt(targetBps)) / 10000n).toString() : null;

  const runAnimation = useCallback((crashPointBps, win) => {
    const crashX = crashPointBps / 10000;
    const peakX = win ? targetBps / 10000 : crashX;
    let current = 1.0;
    const step = () => {
      current = Math.min(peakX, current * 1.04 + 0.01);
      setAnimMult(current);
      if (current < peakX) {
        animRef.current = requestAnimationFrame(step);
      }
    };
    animRef.current = requestAnimationFrame(step);
  }, [targetBps]);

  useEffect(() => () => { if (animRef.current) cancelAnimationFrame(animRef.current); }, []);

  const play = useCallback(async () => {
    setError(''); setResult(null);
    if (!betWei) { setError('Enter a valid amount'); return; }
    if (targetBps < 10100) { setError('Target must be at least 1.01×'); return; }
    setLoading(true);
    setAnimMult(1.00);
    try {
      const res = await api.post('/more-games/crash', { betWei, targetMultiplierBps: targetBps });
      runAnimation(res.data.crashPointBps, res.data.win);
      setTimeout(() => { setResult(res.data); setLoading(false); onRefresh(); }, 800);
    } catch (e) { setError(e.response?.data?.message || 'Game error'); setLoading(false); }
  }, [betWei, targetBps, runAnimation, onRefresh]);

  return (
    <div className="game-card">
      <div className="game-card-header">
        <span className="game-icon">🚀</span>
        <h3>Crash</h3>
        <p className="game-desc">Set your target — cash out before the crash · ~1% house edge</p>
      </div>

      <div className={`crash-visual ${result ? (result.win ? 'crash-win' : 'crash-loss') : ''}`}>
        <div className="crash-track-wrap">
          <div className="crash-track-rail" />
          <div
            className="crash-rocket-icon"
            style={{ bottom: `${Math.min(88, Math.max(4, (animMult - 1) * 46))}%` }}
          >
            {result && !result.win ? '💥' : '🚀'}
          </div>
        </div>
        <span className="crash-mult">{loading || result ? animMult.toFixed(2) : '1.00'}×</span>
        {result && !result.win && <span className="crash-label">CRASHED at {(result.crashPointBps/10000).toFixed(2)}×</span>}
        {result && result.win && <span className="crash-label">✓ Cashed at {(result.targetBps/10000).toFixed(2)}×</span>}
      </div>

      {result && (
        <div className={`game-result ${result.win ? 'game-win' : 'game-loss'}`}>
          {result.win ? `Won! +$${weiToUsd(result.payoutWei)} USDT` : `Crashed at ${(result.crashPointBps/10000).toFixed(2)}× — Lost`}
        </div>
      )}

      <div className="game-controls">
        <div className="crash-target-row">
          <label className="crash-target-label">Target multiplier</label>
          <input
            className="bet-input crash-target-input"
            type="number" min="1.01" step="0.10"
            value={target}
            onChange={e => setTarget(e.target.value)}
            disabled={loading}
          />
          <span className="crash-target-suffix">×</span>
        </div>
        <div className="bet-row">
          <input className="bet-input" type="number" min="0.01" step="0.01" placeholder="Bet (USDT)" value={bet} onChange={e=>setBet(e.target.value)} disabled={loading} />
          <button className="play-btn" onClick={play} disabled={loading||!bet}>{loading?'Flying…':'Launch!'}</button>
        </div>
        {potentialPayout && <p className="game-payout-preview">Potential win: <strong>${weiToUsd(potentialPayout)} USDT</strong></p>}
        {error && <p className="game-error">{error}</p>}
        <p className="game-balance-hint">Balance: ${weiToUsd(balanceWei)} USDT</p>
      </div>
    </div>
  );
}

// ─── Plinko ───────────────────────────────────────────────────────────────────
function Plinko({ balanceWei, cfg, onRefresh }) {
  const [bet, setBet] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [ballPos, setBallPos] = useState(null);
  const [ballDropping, setBallDropping] = useState(false);
  const [ballAtBottom, setBallAtBottom] = useState(false);

  const buckets = cfg?.bucketsBps || [1000,2000,3000,5000,24000,5000,3000,2000,1000];
  const maxBps = Math.max(...buckets);
  const betWei = usdToWei(bet);
  const maxPayout = betWei && maxBps ? ((BigInt(betWei) * BigInt(maxBps)) / 10000n).toString() : null;
  const ballTargetPct = ballPos !== null ? ((ballPos + 0.5) / buckets.length * 100) : 50;

  const play = useCallback(async () => {
    setError(''); setResult(null); setBallPos(null); setBallDropping(false); setBallAtBottom(false);
    if (!betWei) { setError('Enter a valid amount'); return; }
    setLoading(true);
    try {
      const res = await api.post('/more-games/plinko', { betWei });
      setBallPos(res.data.bucketIndex);
      setBallDropping(true);
      requestAnimationFrame(() => requestAnimationFrame(() => setBallAtBottom(true)));
      setTimeout(() => { setResult(res.data); setLoading(false); setBallDropping(false); onRefresh(); }, 950);
    } catch (e) { setError(e.response?.data?.message || 'Game error'); setLoading(false); }
  }, [betWei, onRefresh]);

  return (
    <div className="game-card">
      <div className="game-card-header">
        <span className="game-icon">🎯</span>
        <h3>Plinko</h3>
        {cfg && <p className="game-desc">{cfg.rows}-row board · Max {bpsToX(maxBps)}</p>}
      </div>

      <div className="plinko-board" style={{ position: 'relative' }}>
        {ballDropping && (
          <div
            className="plinko-ball"
            style={{
              left: `${ballTargetPct}%`,
              top: ballAtBottom ? 'calc(100% - 28px)' : '4px',
            }}
          />
        )}
        {cfg && Array.from({length: cfg.rows}).map((_,row) => (
          <div key={row} className="plinko-peg-row">
            {Array.from({length: row + 2}).map((_,p) => <span key={p} className="plinko-peg" />)}
          </div>
        ))}
        <div className="plinko-buckets">
          {buckets.map((bps, i) => (
            <div
              key={i}
              className={`plinko-bucket ${ballPos === i ? 'plinko-bucket-active' : ''} ${result?.bucketIndex === i ? (result.win ? 'bucket-win' : 'bucket-loss') : ''}`}
            >
              <span className="bucket-mult">{bpsToX(bps)}</span>
            </div>
          ))}
        </div>
      </div>

      {result && (
        <div className={`game-result ${result.win ? 'game-win' : 'game-loss'}`}>
          {result.win ? `${bpsToX(result.bucketBps)} — Won $${weiToUsd(result.payoutWei)}!` : `${bpsToX(result.bucketBps)} — No win this time`}
        </div>
      )}

      <div className="game-controls">
        <div className="bet-row">
          <input className="bet-input" type="number" min="0.01" step="0.01" placeholder="Bet (USDT)" value={bet} onChange={e=>setBet(e.target.value)} disabled={loading} />
          <button className="play-btn" onClick={play} disabled={loading||!bet}>{loading?'Dropping…':'Drop!'}</button>
        </div>
        {maxPayout && <p className="game-payout-preview">Max win: <strong>${weiToUsd(maxPayout)} USDT</strong></p>}
        {error && <p className="game-error">{error}</p>}
        <p className="game-balance-hint">Balance: ${weiToUsd(balanceWei)} USDT</p>
      </div>
    </div>
  );
}

// ─── Keno ─────────────────────────────────────────────────────────────────────
function Keno({ balanceWei, cfg, onRefresh }) {
  const [bet, setBet] = useState('');
  const [picks, setPicks] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [visibleDrawn, setVisibleDrawn] = useState(new Set());
  const revealIntervalRef = useRef(null);
  useEffect(() => () => clearInterval(revealIntervalRef.current), []);

  const poolSize = cfg?.poolSize || 20;
  const pickCount = cfg?.pickCount || 4;
  const payouts = cfg?.payoutTableBps || [0, 0, 3000, 20000, 80000];
  const betWei = usdToWei(bet);
  const maxBps = Math.max(...payouts);
  const maxPayout = betWei && maxBps ? ((BigInt(betWei) * BigInt(maxBps)) / 10000n).toString() : null;

  const togglePick = (n) => {
    if (loading || result) return;
    setPicks(prev => {
      const next = new Set(prev);
      if (next.has(n)) next.delete(n);
      else if (next.size < pickCount) next.add(n);
      return next;
    });
  };

  const play = useCallback(async () => {
    setError('');
    if (!betWei) { setError('Enter a valid amount'); return; }
    if (picks.size !== pickCount) { setError(`Pick exactly ${pickCount} numbers`); return; }
    setLoading(true); setResult(null); setVisibleDrawn(new Set());
    try {
      const res = await api.post('/more-games/keno', { betWei, picks: [...picks] });
      const drawn = res.data.drawn;
      let i = 0;
      revealIntervalRef.current = setInterval(() => {
        i++;
        setVisibleDrawn(new Set(drawn.slice(0, i)));
        if (i >= drawn.length) {
          clearInterval(revealIntervalRef.current);
          setResult(res.data);
          setLoading(false);
          onRefresh();
        }
      }, 130);
    } catch (e) { setError(e.response?.data?.message || 'Game error'); setLoading(false); }
  }, [betWei, picks, pickCount, onRefresh]);

  const resetGame = () => {
    clearInterval(revealIntervalRef.current);
    setResult(null); setPicks(new Set()); setVisibleDrawn(new Set());
  };

  const drawnSet = result ? new Set(result.drawn) : visibleDrawn;

  return (
    <div className="game-card game-card-wide">
      <div className="game-card-header">
        <span className="game-icon">🔢</span>
        <h3>Keno</h3>
        {cfg && <p className="game-desc">Pick {pickCount} from {poolSize} · {cfg.drawCount} drawn · Max {bpsToX(maxBps)}</p>}
      </div>

      <div className="keno-grid">
        {Array.from({length: poolSize}, (_,i) => i + 1).map(n => {
          const isPick = picks.has(n);
          const isDrawn = drawnSet.has(n);
          const isMatch = isPick && isDrawn;
          return (
            <button
              key={n}
              className={`keno-ball ${isPick ? 'keno-pick' : ''} ${isDrawn ? 'keno-drawn' : ''} ${isMatch ? 'keno-match' : ''}`}
              onClick={() => togglePick(n)}
              disabled={loading}
            >
              {n}
            </button>
          );
        })}
      </div>

      {cfg && (
        <div className="keno-payout-table">
          {payouts.map((bps, k) => (
            <span key={k} className={`keno-payout-item ${result?.matches === k ? 'keno-payout-active' : ''}`}>
              {k} match{k !== 1 ? 'es' : ''}: <strong>{bpsToX(bps)}</strong>
            </span>
          ))}
        </div>
      )}

      {result && (
        <div className={`game-result ${result.win ? 'game-win' : 'game-loss'}`}>
          {result.matches} match{result.matches !== 1 ? 'es' : ''} — {result.win ? `Won $${weiToUsd(result.payoutWei)} USDT!` : 'No win this round'}
        </div>
      )}

      <div className="game-controls">
        <p className="keno-pick-hint">{picks.size}/{pickCount} numbers selected</p>
        <div className="bet-row">
          <input className="bet-input" type="number" min="0.01" step="0.01" placeholder="Bet (USDT)" value={bet} onChange={e=>setBet(e.target.value)} disabled={loading} />
          <button className="play-btn" onClick={result ? resetGame : play} disabled={loading || (!result && (picks.size !== pickCount || !bet))}>
            {loading ? 'Drawing…' : result ? 'New Round' : 'Play!'}
          </button>
        </div>
        {maxPayout && !result && <p className="game-payout-preview">Max win: <strong>${weiToUsd(maxPayout)} USDT</strong></p>}
        {error && <p className="game-error">{error}</p>}
        <p className="game-balance-hint">Balance: ${weiToUsd(balanceWei)} USDT</p>
      </div>
    </div>
  );
}

// ─── Roulette ─────────────────────────────────────────────────────────────────
const RED_POCKETS = new Set([1,3,5,7,9,12,14,16,18,19,21,23,25,27,30,32,34,36]);

function getPocketColor(n) {
  if (n === 0) return 'green';
  return RED_POCKETS.has(n) ? 'red' : 'black';
}

function RouletteWheel({ spinning, landedPocket }) {
  // Pocket order on a real European wheel
  const wheelOrder = [0,32,15,19,4,21,2,25,17,34,6,27,13,36,11,30,8,23,10,5,24,16,33,1,20,14,31,9,22,18,29,7,28,12,35,3,26];
  const count = wheelOrder.length; // 37
  const segAngle = 360 / count;

  return (
    <div className={`roulette-wheel-wrap${spinning ? ' spinning' : ''}`}>
      <div className="roulette-wheel">
        {wheelOrder.map((n, i) => {
          const color = getPocketColor(n);
          const rotate = i * segAngle;
          const isLanded = !spinning && landedPocket !== null && n === landedPocket;
          return (
            <div
              key={n}
              className={`roulette-segment roulette-seg-${color}${isLanded ? ' roulette-seg-landed' : ''}`}
              style={{ transform: `rotate(${rotate}deg)`, '--seg-angle': `${segAngle}deg` }}
            >
              <span className="roulette-seg-num">{n}</span>
            </div>
          );
        })}
        <div className="roulette-center">🎰</div>
      </div>
      {/* Ball indicator */}
      {!spinning && landedPocket !== null && (
        <div className={`roulette-result-badge roulette-badge-${getPocketColor(landedPocket)}`}>
          {landedPocket}
        </div>
      )}
      {spinning && <div className="roulette-spinning-msg">Spinning…</div>}
    </div>
  );
}

const BET_TYPES = [
  { id: 'red',    label: 'Red',      payoutLabel: '~1.94×' },
  { id: 'black',  label: 'Black',    payoutLabel: '~1.94×' },
  { id: 'odd',    label: 'Odd',      payoutLabel: '~1.94×' },
  { id: 'even',   label: 'Even',     payoutLabel: '~1.94×' },
  { id: 'dozen1', label: '1st Dozen (1–12)',  payoutLabel: '~2.94×' },
  { id: 'dozen2', label: '2nd Dozen (13–24)', payoutLabel: '~2.94×' },
  { id: 'dozen3', label: '3rd Dozen (25–36)', payoutLabel: '~2.94×' },
  { id: 'col1',   label: 'Column 1',  payoutLabel: '~2.94×' },
  { id: 'col2',   label: 'Column 2',  payoutLabel: '~2.94×' },
  { id: 'col3',   label: 'Column 3',  payoutLabel: '~2.94×' },
  { id: 'straight', label: 'Straight Up (exact number)', payoutLabel: '35×' },
];

function Roulette({ balanceWei, onRefresh }) {
  const [bet, setBet]               = useState('');
  const [betType, setBetType]       = useState('red');
  const [straightNum, setStraightNum] = useState('0');
  const [spinning, setSpinning]     = useState(false);
  const [landedPocket, setLandedPocket] = useState(null);
  const [result, setResult]         = useState(null);
  const [error, setError]           = useState('');
  const [loading, setLoading]       = useState(false);

  const play = useCallback(async () => {
    setError(''); setResult(null); setLandedPocket(null);
    const betWei = usdToWei(bet);
    if (!betWei) { setError('Enter a valid bet amount'); return; }
    if (betType === 'straight') {
      const n = parseInt(straightNum, 10);
      if (isNaN(n) || n < 0 || n > 36) { setError('Pick a number 0–36 for straight bet'); return; }
    }
    setLoading(true);
    setSpinning(true);
    try {
      const body = { betWei, betType };
      if (betType === 'straight') body.number = parseInt(straightNum, 10);
      const res = await api.post('/more-games/roulette', body);
      // Let wheel spin for 2s then reveal
      setTimeout(() => {
        setSpinning(false);
        setLandedPocket(res.data.pocket);
        setResult(res.data);
        onRefresh();
      }, 2000);
    } catch (e) {
      setSpinning(false);
      setError(e.response?.data?.message || 'Game error');
    } finally {
      setLoading(false);
    }
  }, [bet, betType, straightNum, onRefresh]);

  const resetGame = () => { setResult(null); setLandedPocket(null); setError(''); };

  return (
    <div className="game-card roulette-card">
      <div className="game-card-header">
        <span className="game-icon">🎰</span>
        <h3>Roulette</h3>
        <span className="game-badge">European · ~2.7% edge</span>
      </div>

      <RouletteWheel spinning={spinning} landedPocket={landedPocket} />

      {result && (
        <div className={`game-result ${result.win ? 'game-win' : 'game-loss'}`}>
          Pocket <strong>{result.pocket}</strong> ({getPocketColor(result.pocket).toUpperCase()}) —{' '}
          {result.win ? `Won $${weiToUsd(result.payoutWei)} USDT!` : 'Better luck next time'}
        </div>
      )}

      <div className="game-controls">
        {/* Bet type selector */}
        <div className="roulette-bet-types">
          {BET_TYPES.map(bt => (
            <button
              key={bt.id}
              className={`roulette-bet-btn${betType === bt.id ? ' active' : ''} roulette-btn-${bt.id.replace(/\d/g, '')}`}
              onClick={() => setBetType(bt.id)}
              disabled={loading || spinning}
            >
              {bt.label} <span className="roulette-payout-tag">{bt.payoutLabel}</span>
            </button>
          ))}
        </div>

        {betType === 'straight' && (
          <div className="roulette-straight-row">
            <label>Number (0–36):</label>
            <input
              type="number" min="0" max="36" step="1"
              className="bet-input roulette-num-input"
              value={straightNum}
              onChange={e => setStraightNum(e.target.value)}
              disabled={loading || spinning}
            />
          </div>
        )}

        <div className="bet-row">
          <input
            className="bet-input"
            type="number" min="0.01" step="0.01"
            placeholder="Bet (USDT)"
            value={bet}
            onChange={e => setBet(e.target.value)}
            disabled={loading || spinning}
          />
          <button
            className="play-btn"
            onClick={result ? resetGame : play}
            disabled={spinning || loading || (!result && !bet)}
          >
            {spinning ? 'Spinning…' : result ? 'Spin Again' : 'Spin!'}
          </button>
        </div>
        {error && <p className="game-error">{error}</p>}
        <p className="game-balance-hint">Balance: ${weiToUsd(balanceWei)} USDT</p>
      </div>
    </div>
  );
}

// ─── Blackjack ────────────────────────────────────────────────────────────────
const CARD_RANKS  = ['A','2','3','4','5','6','7','8','9','10','J','Q','K'];
const CARD_SUITS  = ['♠','♥','♦','♣'];
const SUIT_COLORS = ['#e8e8e8','#e05c5c','#e05c5c','#e8e8e8'];


function cardColor(card) { return SUIT_COLORS[Math.floor(card / 13)]; }

function BJCard({ card, faceDown }) {
  if (faceDown) return <div className="bj-card bj-card-back" />;
  return (
    <div className="bj-card" style={{ color: cardColor(card) }}>
      <span className="bj-card-rank">{CARD_RANKS[card % 13]}</span>
      <span className="bj-card-suit">{CARD_SUITS[Math.floor(card / 13)]}</span>
    </div>
  );
}

const BJ_RESULT_LABELS = {
  player_blackjack: '🃏 Blackjack! You win!',
  dealer_blackjack: 'Dealer has Blackjack',
  player_bust:      'Bust! You went over 21',
  dealer_bust:      'Dealer busts — You win!',
  player_win:       'You win!',
  dealer_win:       'Dealer wins',
  push:             'Push — Tie',
};

function Blackjack({ balanceWei, onRefresh }) {
  const [bet, setBet]               = useState('');
  const [session, setSession]       = useState(null);
  const [playerCards, setPlayerCards] = useState([]);
  const [dealerVisible, setDealerVisible] = useState([]);
  const [dealerCards, setDealerCards]   = useState([]);
  const [playerTotal, setPlayerTotal]   = useState(0);
  const [dealerTotal, setDealerTotal]   = useState(0);
  const [result, setResult]         = useState(null);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');

  useEffect(() => {
    api.get('/more-games/blackjack/session').then(res => {
      if (res.data.session) {
        const s = res.data.session;
        setSession(s);
        setPlayerCards(s.playerCards);
        setDealerVisible(s.dealerVisible);
        setPlayerTotal(s.playerTotal);
        setDealerTotal(s.dealerVisibleTotal);
      }
    }).catch(() => {});
  }, []);

  const deal = useCallback(async () => {
    setError(''); setResult(null);
    const betWei = usdToWei(bet);
    if (!betWei) { setError('Enter a valid amount'); return; }
    setLoading(true);
    try {
      const res = await api.post('/more-games/blackjack/deal', { betWei });
      if (res.data.finished) {
        setPlayerCards(res.data.playerCards);
        setDealerCards(res.data.dealerCards);
        setDealerVisible([]);
        setPlayerTotal(res.data.playerTotal);
        setDealerTotal(res.data.dealerTotal);
        setResult(res.data);
        setSession(null);
      } else {
        setSession({ sessionId: res.data.sessionId, betWei });
        setPlayerCards(res.data.playerCards);
        setDealerVisible(res.data.dealerVisible);
        setDealerCards([]);
        setPlayerTotal(res.data.playerTotal);
        setDealerTotal(res.data.dealerVisibleTotal);
        setResult(null);
      }
      onRefresh();
    } catch (e) { setError(e.response?.data?.message || 'Game error'); }
    finally { setLoading(false); }
  }, [bet, onRefresh]);

  const hit = useCallback(async () => {
    if (!session || loading) return;
    setLoading(true);
    try {
      const res = await api.post('/more-games/blackjack/hit', { sessionId: session.sessionId });
      setPlayerCards(res.data.playerCards);
      setPlayerTotal(res.data.playerTotal);
      if (res.data.finished) {
        setDealerCards(res.data.dealerCards);
        setDealerVisible([]);
        setDealerTotal(res.data.dealerTotal);
        setResult(res.data);
        setSession(null);
        onRefresh();
      }
    } catch (e) { setError(e.response?.data?.message || 'Game error'); }
    finally { setLoading(false); }
  }, [session, loading, onRefresh]);

  const stand = useCallback(async () => {
    if (!session || loading) return;
    setLoading(true);
    try {
      const res = await api.post('/more-games/blackjack/stand', { sessionId: session.sessionId });
      setPlayerCards(res.data.playerCards);
      setDealerCards(res.data.dealerCards);
      setDealerVisible([]);
      setPlayerTotal(res.data.playerTotal);
      setDealerTotal(res.data.dealerTotal);
      setResult(res.data);
      setSession(null);
      onRefresh();
    } catch (e) { setError(e.response?.data?.message || 'Game error'); }
    finally { setLoading(false); }
  }, [session, loading, onRefresh]);

  const isWin  = result && ['player_blackjack','dealer_bust','player_win'].includes(result.result);
  const isPush = result?.result === 'push';
  const showDealerCards = result ? dealerCards : dealerVisible;
  const showDealerHole  = !result && dealerVisible.length > 0;

  return (
    <div className="game-card game-card-wide">
      <div className="game-card-header">
        <span className="game-icon">🃏</span>
        <div>
          <h3 className="game-title">Blackjack</h3>
          <p className="game-desc">Beat the dealer to 21. Blackjack pays 1.5×</p>
        </div>
      </div>

      {(playerCards.length > 0 || showDealerCards.length > 0) && (
        <div className="bj-table">
          <div className="bj-hand">
            <span className="bj-label">Dealer {result ? `— ${dealerTotal}` : `— ${dealerTotal}+?`}</span>
            <div className="bj-cards">
              {showDealerCards.map((c, i) => <BJCard key={i} card={c} />)}
              {showDealerHole && <BJCard faceDown />}
            </div>
          </div>
          <div className="bj-hand">
            <span className="bj-label">You — {playerTotal}</span>
            <div className="bj-cards">
              {playerCards.map((c, i) => <BJCard key={i} card={c} />)}
            </div>
          </div>
        </div>
      )}

      {result && (
        <div className={`game-result ${isWin ? 'game-win' : isPush ? 'game-push' : 'game-loss'}`}>
          <span className="result-label">{BJ_RESULT_LABELS[result.result] || result.result}</span>
          {BigInt(result.payoutWei || '0') > 0n && (
            <span className="result-payout">+${weiToUsd(result.payoutWei)}</span>
          )}
        </div>
      )}

      <div className="game-controls">
        {session ? (
          <div className="bj-actions">
            <button className="play-btn" onClick={hit} disabled={loading}>Hit</button>
            <button className="play-btn bj-stand-btn" onClick={stand} disabled={loading}>Stand</button>
          </div>
        ) : (
          <>
            <input className="bet-input" type="number" placeholder="Bet (USDT)" value={bet}
              onChange={e => setBet(e.target.value)} min="0" step="0.01" />
            <button className="play-btn" onClick={deal} disabled={loading}>
              {result ? 'Deal Again' : 'Deal'}
            </button>
          </>
        )}
        {error && <p className="game-error">{error}</p>}
        <p className="game-balance-hint">Balance: ${weiToUsd(balanceWei)} USDT</p>
      </div>
    </div>
  );
}

// ─── Video Poker ───────────────────────────────────────────────────────────────
const VP_HAND_NAMES = {
  royalFlush: 'Royal Flush', straightFlush: 'Straight Flush',
  fourOfAKind: 'Four of a Kind', fullHouse: 'Full House',
  flush: 'Flush', straight: 'Straight',
  threeOfAKind: 'Three of a Kind', twoPair: 'Two Pair',
  jacksOrBetter: 'Jacks or Better', nothing: 'No Win',
};

const VP_PAYOUT_ROWS = [
  { name: 'Royal Flush',     cfgKey: 'royalFlushBps',    handKey: 'royalFlush'    },
  { name: 'Straight Flush',  cfgKey: 'straightFlushBps', handKey: 'straightFlush' },
  { name: 'Four of a Kind',  cfgKey: 'fourOfAKindBps',   handKey: 'fourOfAKind'   },
  { name: 'Full House',      cfgKey: 'fullHouseBps',      handKey: 'fullHouse'     },
  { name: 'Flush',           cfgKey: 'flushBps',          handKey: 'flush'         },
  { name: 'Straight',        cfgKey: 'straightBps',       handKey: 'straight'      },
  { name: 'Three of a Kind', cfgKey: 'threeOfAKindBps',   handKey: 'threeOfAKind'  },
  { name: 'Two Pair',        cfgKey: 'twoPairBps',        handKey: 'twoPair'       },
  { name: 'Jacks or Better', cfgKey: 'jacksOrBetterBps',  handKey: 'jacksOrBetter' },
];

function VPCard({ card, held, clickable, onClick }) {
  return (
    <div className={`vp-card-wrap${clickable ? ' vp-clickable' : ''}`} onClick={onClick}>
      <div className={`vp-card${held ? ' vp-card-held' : ''}`} style={{ color: cardColor(card) }}>
        <span className="vp-rank">{CARD_RANKS[card % 13]}</span>
        <span className="vp-suit">{CARD_SUITS[Math.floor(card / 13)]}</span>
      </div>
      <div className="vp-hold-label">{held ? 'HOLD' : ''}</div>
    </div>
  );
}

function VideoPoker({ balanceWei, cfg, onRefresh }) {
  const [bet, setBet]           = useState('');
  const [session, setSession]   = useState(null);
  const [hand, setHand]         = useState([]);
  const [held, setHeld]         = useState([false,false,false,false,false]);
  const [result, setResult]     = useState(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');

  useEffect(() => {
    api.get('/more-games/video-poker/session').then(res => {
      if (res.data.session) {
        const s = res.data.session;
        setSession(s);
        setHand(s.hand);
        setHeld([false,false,false,false,false]);
      }
    }).catch(() => {});
  }, []);

  const deal = useCallback(async () => {
    setError(''); setResult(null);
    const betWei = usdToWei(bet);
    if (!betWei) { setError('Enter a valid amount'); return; }
    setLoading(true);
    try {
      const res = await api.post('/more-games/video-poker/deal', { betWei });
      setSession({ sessionId: res.data.sessionId, betWei });
      setHand(res.data.hand);
      setHeld([false,false,false,false,false]);
      setResult(null);
      onRefresh();
    } catch (e) { setError(e.response?.data?.message || 'Game error'); }
    finally { setLoading(false); }
  }, [bet, onRefresh]);

  const toggleHold = (i) => {
    if (!session || result) return;
    setHeld(prev => { const n = [...prev]; n[i] = !n[i]; return n; });
  };

  const draw = useCallback(async () => {
    if (!session || loading) return;
    setLoading(true);
    const holdIndices = held.map((h, i) => h ? i : -1).filter(i => i >= 0);
    try {
      const res = await api.post('/more-games/video-poker/draw', { sessionId: session.sessionId, hold: holdIndices });
      setHand(res.data.hand);
      setResult(res.data);
      setSession(null);
      onRefresh();
    } catch (e) { setError(e.response?.data?.message || 'Game error'); }
    finally { setLoading(false); }
  }, [session, held, loading, onRefresh]);

  const activeHandName = result ? VP_HAND_NAMES[result.handName] : null;

  return (
    <div className="game-card game-card-wide">
      <div className="game-card-header">
        <span className="game-icon">🎴</span>
        <div>
          <h3 className="game-title">Video Poker</h3>
          <p className="game-desc">Jacks or Better — click cards to hold, then draw</p>
        </div>
      </div>

      <div className="vp-payout-table">
        {VP_PAYOUT_ROWS.map(row => {
          const bps = cfg?.[row.cfgKey];
          const mult = bps != null ? (bps / 10000).toFixed(0) + '×' : '—';
          return (
            <div key={row.handKey} className={`vp-row${activeHandName === row.name ? ' vp-row-active' : ''}`}>
              <span>{row.name}</span><span>{mult}</span>
            </div>
          );
        })}
      </div>

      {hand.length > 0 && (
        <div className="vp-hand">
          {hand.map((card, i) => (
            <VPCard key={i} card={card} held={held[i]} clickable={!!session} onClick={() => toggleHold(i)} />
          ))}
        </div>
      )}

      {result && (
        <div className={`game-result ${result.win ? 'game-win' : 'game-loss'}`}>
          <span className="result-label">{VP_HAND_NAMES[result.handName] || 'No Win'}</span>
          {result.win && <span className="result-payout">+${weiToUsd(result.payoutWei)}</span>}
        </div>
      )}

      <div className="game-controls">
        {session ? (
          <button className="play-btn" onClick={draw} disabled={loading}>Draw</button>
        ) : (
          <>
            <input className="bet-input" type="number" placeholder="Bet (USDT)" value={bet}
              onChange={e => setBet(e.target.value)} min="0" step="0.01" />
            <button className="play-btn" onClick={deal} disabled={loading}>
              {result ? 'Deal Again' : 'Deal'}
            </button>
          </>
        )}
        {error && <p className="game-error">{error}</p>}
        <p className="game-balance-hint">Balance: ${weiToUsd(balanceWei)} USDT</p>
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
const MoreGamesSection = ({ vaultBalance, onBalanceRefresh }) => {
  const [gameConfig, setGameConfig] = useState(null);
  const { isAuthenticated, isLoading: authLoading, login } = useAuth();
  const { isConnected } = useWeb3();
  const [signing, setSigning] = useState(false);

  useEffect(() => {
    api.get('/play-vault/config')
      .then(res => setGameConfig(res.data))
      .catch(() => {});
  }, []);

  if (vaultBalance === undefined || !isConnected) {
    return <div className="casino-section"><p className="casino-connect-msg">Connect your wallet to play.</p></div>;
  }

  if (authLoading || signing) {
    return <div className="casino-section"><p className="casino-connect-msg">Signing in…</p></div>;
  }

  if (!isAuthenticated) {
    const handleSign = async () => {
      setSigning(true);
      try { await login(); } catch (_) {}
      finally { setSigning(false); }
    };
    return (
      <div className="casino-section">
        <div className="casino-auth-prompt">
          <p>Sign a message with your wallet to play games.</p>
          <button className="play-btn" onClick={handleSign}>Sign In to Play</button>
        </div>
      </div>
    );
  }

  return (
    <div className="casino-section">
      <div className="casino-games">
        <Crash balanceWei={vaultBalance} cfg={gameConfig?.crash} onRefresh={onBalanceRefresh} />
        <Plinko balanceWei={vaultBalance} cfg={gameConfig?.plinko} onRefresh={onBalanceRefresh} />
        <Mines balanceWei={vaultBalance} cfg={gameConfig?.mines} onRefresh={onBalanceRefresh} />
        <Keno balanceWei={vaultBalance} cfg={gameConfig?.keno} onRefresh={onBalanceRefresh} />
        <Roulette balanceWei={vaultBalance} cfg={gameConfig?.roulette} onRefresh={onBalanceRefresh} />
        <Blackjack balanceWei={vaultBalance} cfg={gameConfig?.blackjack} onRefresh={onBalanceRefresh} />
        <VideoPoker balanceWei={vaultBalance} cfg={gameConfig?.videoPoker} onRefresh={onBalanceRefresh} />
      </div>
    </div>
  );
};

export default MoreGamesSection;
